import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Inbox, Mail, Phone, Building, Reply, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppLayout";
import { api, formatErr } from "@/lib/api";

const KIND = { lender: "Lender", agency: "Government agency", landlord: "Landlord", other: "Other" };
const when = (s) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function ReplyBox({ i, onSent }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/inquiries/${i.inquiry_id}/reply`, { message: msg }); toast.success(`Reply sent to ${i.name}`); onSent(data); setMsg(""); setOpen(false); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid={`inquiry-reply-button-${i.inquiry_id}`}><Reply className="h-4 w-4 mr-1.5" />Reply</Button>;
  return (
    <div className="w-full mt-2">
      <Textarea rows={4} maxLength={3000} placeholder={`Write your reply to ${i.name}. Their reply will come to your email.`} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid={`inquiry-reply-input-${i.inquiry_id}`} />
      <div className="flex gap-2 mt-2">
        <Button size="sm" disabled={busy || msg.trim().length < 5} onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`inquiry-reply-send-${i.inquiry_id}`}>{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send reply</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function InquiryCard({ i: initial }) {
  const [i, setI] = useState(initial);
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-6 border-l-4 ${i.read ? "border-l-slate-300" : "border-l-amber-500"}`} data-testid={`inquiry-${i.inquiry_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><h3 className="font-semibold text-slate-900">{i.name}</h3>{!i.read && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`inquiry-new-${i.inquiry_id}`}>New</span>}</div>
          <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Building className="h-3.5 w-3.5" />{i.organization} · {KIND[i.kind] || i.kind}</p>
        </div>
        <span className="text-xs text-slate-400">{when(i.at)}</span>
      </div>
      <p className="text-sm text-slate-700 mt-4 whitespace-pre-wrap">{i.message}</p>
      {(i.replies || []).map((r, n) => (
        <div key={n} className="mt-3 ml-4 border-l-2 border-emerald-400 pl-3 text-sm" data-testid={`inquiry-reply-${i.inquiry_id}-${n}`}>
          <div className="text-xs text-slate-400">You replied · {when(r.at)}</div>
          <p className="text-slate-700 whitespace-pre-wrap">{r.message}</p>
        </div>
      ))}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
        <ReplyBox i={i} onSent={(r) => setI({ ...i, replies: [...(i.replies || []), r] })} />
        <a href={`mailto:${i.email}`} className="flex items-center gap-1.5 text-emerald-700 font-semibold hover:underline" data-testid={`inquiry-email-${i.inquiry_id}`}><Mail className="h-4 w-4" />{i.email}</a>
        {i.phone && <a href={`tel:${i.phone}`} className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900"><Phone className="h-4 w-4" />{i.phone}</a>}
      </div>
    </div>
  );
}

export default function Inquiries() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/inquiries").then((r) => setItems(r.data)).catch((e) => setErr(formatErr(e))); }, []);
  return (
    <div>
      <PageHeader eyebrow="Peer Verified directory" title="Lender inquiries">Messages from lenders, agencies and landlords who found your business on the public Badge Wall. Reply straight to their email.</PageHeader>
      {err ? <p className="text-slate-500" data-testid="inquiries-error">{err}</p> : !items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="inquiries-empty">
          <Inbox className="h-8 w-8 mx-auto text-amber-500" />
          <p className="mt-4 text-slate-500">No inquiries yet. List your Peer Verified badge on the public wall so lenders can find you.</p>
          <Link to="/dashboard/pros" className="text-emerald-700 font-semibold text-sm mt-3 inline-block" data-testid="inquiries-go-to-badge-link">Manage my badge →</Link>
        </div>
      ) : <div className="space-y-3" data-testid="inquiries-list">{items.map((i) => <InquiryCard key={i.inquiry_id} i={i} />)}</div>}
    </div>
  );
}
